'use client';

import classNames from 'classnames';
import Image from 'next/image';
import { type JSX, useState } from 'react';

import { Search } from '@videoblocks/react-icons';

import TopNavSearchBar from './TopNavSearchBar';
import DesktopNavActions from './desktop/DesktopNavActions';
import DesktopNavItem from './desktop/DesktopNavItem';
import MobileNav from './mobile/MobileNav';
import TabletNavActions from './tablet/TabletNavActions';
import type { DesktopNavConfig, MobileNavConfig, TabletNavConfig } from './types';

interface TopNavigationBarProps {
    desktop: DesktopNavConfig;
    tablet: TabletNavConfig;
    mobile: MobileNavConfig;
    darkHeader?: boolean;
    showSearchByDefault?: boolean;
    showMobileSearchBar?: boolean;
}

export default function TopNavigationBar({
    desktop,
    tablet,
    mobile,
    darkHeader = false,
    showSearchByDefault = false,
    showMobileSearchBar = true,
}: TopNavigationBarProps): JSX.Element {
    const [isSearchExpanded, setIsSearchExpanded] = useState(showSearchByDefault);

    return (
        <nav
            aria-label="Main navigation"
            data-cta="topnav"
            className={classNames(
                'sticky top-0 z-50 w-full flex flex-col gap-4 px-4 py-4 topnav-xl:px-6 topnav-xl:py-4 border-b dark:bg-neutral-dark-100 dark:border-neutral-dark-200 border-gray-200 bg-white',
                {
                    'topnav-md:pb-0': isSearchExpanded,
                },
            )}
        >
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-5">
                    <a href="/" className="shrink-0" aria-label="Storyblocks home">
                        {/* Wordmark — shown 0-869 AND 1258+ (mobile, full desktop) */}
                        <Image
                            src={darkHeader ? '/assets/images/sb-logo-white.svg' : '/assets/images/sb-logo.svg'}
                            alt="Storyblocks"
                            width={151}
                            height={32}
                            priority
                            className="block topnav-sm:hidden topnav-xl:block"
                        />
                        {/* Small-circle logo — shown 870-1257 (tablet + small desktop).
                            CSS-cropped from the wordmark so no new asset is needed. */}
                        <span className="hidden topnav-sm:block topnav-xl:hidden h-8 w-8 overflow-hidden">
                            <Image
                                src={darkHeader ? '/assets/images/sb-logo-white.svg' : '/assets/images/sb-logo.svg'}
                                alt=""
                                aria-hidden
                                width={151}
                                height={32}
                                className="block h-8 w-auto max-w-none"
                            />
                        </span>
                    </a>

                    {/* Tablet nav items — visible 870 to 1025 */}
                    <div className="hidden items-center topnav-sm:flex topnav-md:hidden">
                        {tablet.navItems.map((item) => (
                            <DesktopNavItem key={item.id} config={item} darkHeader={darkHeader} />
                        ))}
                    </div>

                    {/* Desktop nav items + search icon — visible 1026+. Business/Resources hidden 1026-1257. */}
                    <div className="hidden items-center topnav-md:flex">
                        {desktop.navItems.map((item) => {
                            const hiddenAtSmallDesktop = item.id === 'businesses' || item.id === 'resources';
                            return (
                                <span
                                    key={item.id}
                                    className={hiddenAtSmallDesktop ? 'hidden topnav-xl:inline-flex' : 'inline-flex'}
                                >
                                    <DesktopNavItem config={item} darkHeader={darkHeader} />
                                </span>
                            );
                        })}
                        {!isSearchExpanded && (
                            <button
                                type="button"
                                onClick={() => setIsSearchExpanded(true)}
                                aria-label="Open search"
                                className={classNames('ml-2 p-2', {
                                    'text-white hover:text-gray-300': darkHeader,
                                    'text-gray-600 hover:text-gray-900': !darkHeader,
                                })}
                            >
                                <Search className="w-4 h-4 fill-current" />
                            </button>
                        )}
                    </div>
                </div>

                {/* Right side — Desktop actions + Tablet actions + Mobile hamburger */}
                <div className="flex items-center gap-4">
                    <DesktopNavActions items={desktop.actions} darkHeader={darkHeader} />
                    <TabletNavActions
                        actions={tablet.actions}
                        hamburgerNavItems={tablet.hamburgerNavItems}
                        overlayActions={tablet.overlayActions}
                        darkHeader={darkHeader}
                    />
                    <MobileNav config={mobile} darkHeader={darkHeader} />
                </div>
            </div>
            {/* Desktop (1026+): expanded search bar */}
            {isSearchExpanded && (
                <div className="hidden topnav-md:flex justify-center pb-2 px-6">
                    <TopNavSearchBar onCollapse={() => setIsSearchExpanded(false)} showCloseButton />
                </div>
            )}
            {/* Tablet + Mobile (0-1025): search bar (suppressible for lite layouts) */}
            {showMobileSearchBar && (
                <div className="topnav-md:hidden">
                    <TopNavSearchBar />
                </div>
            )}
        </nav>
    );
}
